import { useTranslation } from 'react-i18next';
import { LedgerRow, LEDGER_COLS } from './LedgerRow';
import type { Expense } from '../../../types';
import EmptyState from '../../ui/EmptyState';
import { Plus, Wallet } from '../../ui/Icon';

interface Props {
  expenses: Expense[];
  /** The trip's whole ledger, so an empty table can tell "no expenses yet"
   *  apart from "nothing matches the filters". */
  totalCount: number;
  onSelect: (e: Expense) => void;
  /** Absent for read-only viewers. */
  onAdd?: () => void;
}

export function LedgerTable({ expenses, totalCount, onSelect, onAdd }: Props) {
  const { t } = useTranslation();

  const showEmpty = totalCount === 0;

  if (showEmpty) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-card">
        <EmptyState
          icon={Wallet}
          title={t('expenses.empty.title')}
          body={t('expenses.empty.desc')}
          action={onAdd && { label: t('expenses.empty.addFirst'), icon: Plus, onClick: onAdd }}
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-card">
      {/* Table header — the phone layout stacks each field with its own meaning
          on screen, so column titles would label nothing. */}
      <div className={`hidden md:grid ${LEDGER_COLS} gap-3 px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30`}>
        {[
          t('expenses.table.date'), t('expenses.table.item'), t('expenses.table.category'),
          t('expenses.table.paidBy'), t('expenses.table.split'), t('expenses.table.amount'),
        ].map((h, i) => (
          <span key={i} className="font-mono text-[11px] tracking-[0.1em] uppercase text-slate-400">
            {h}
          </span>
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
        {expenses.length === 0 && (
          <EmptyState size="inline" icon={Wallet} title={t('expenses.table.noFilter')} />
        )}
        {expenses.map(expense => (
          <LedgerRow key={expense.id} expense={expense} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}
